import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import agentService from '../services/agentService';
import mitreService from '../services/mitreService';
import { useLanguage } from '../contexts/LanguageContext';

function AgentDetail() {
  const { t } = useLanguage();
  const { agentId } = useParams();
  const [searchParams] = useSearchParams();
  const [agent, setAgent] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [techniques, setTechniques] = useState([]);

  // Task form state
  const [taskForm, setTaskForm] = useState({
    name: '',
    executorType: 'shell',
    command: '',
    timeout: 300,
    cleanupCommand: '',
    techniqueId: ''
  });
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [taskResult, setTaskResult] = useState(null);

  // Execute tab: live result polling
  const [executeResult, setExecuteResult] = useState(null);
  const executePollerRef = useRef(null);

  // Console state
  const [consoleHistory, setConsoleHistory] = useState([]);
  const [consoleCommand, setConsoleCommand] = useState('');
  const [consoleExecutor, setConsoleExecutor] = useState('powershell');
  const [consoleSending, setConsoleSending] = useState(false);
  const consoleEndRef = useRef(null);
  const consolePollers = useRef({});

  // Activity filter state
  const [activityFilter, setActivityFilter] = useState('all');

  const fetchAgent = useCallback(async () => {
    try {
      const [agentData, tasksData] = await Promise.all([
        agentService.getAgent(agentId),
        agentService.getAgentTasks(agentId, { limit: 50 })
      ]);
      setAgent(agentData);
      setTasks(Array.isArray(tasksData) ? tasksData : []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    fetchAgent();
    const interval = setInterval(fetchAgent, 10000);
    return () => clearInterval(interval);
  }, [fetchAgent]);

  useEffect(() => {
    const loadTechniques = async () => {
      try {
        const data = await mitreService.getTechniques();
        setTechniques(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load techniques:', err);
      }
    };
    loadTechniques();
  }, []);

  // Load console history from existing tasks on tab mount
  useEffect(() => {
    if (activeTab === 'console' && tasks.length > 0) {
      const consoleTasks = tasks
        .filter(t => t.name?.startsWith('[Console]'))
        .slice(0, 20)
        .reverse();

      const history = consoleTasks.map(t => ({
        command: t.command,
        executor: t.executorType,
        status: t.status,
        taskId: t.taskId,
        timestamp: t.createdAt,
        stdout: t.result?.stdout || '',
        stderr: t.result?.stderr || '',
        exitCode: t.result?.exitCode,
        durationMs: t.result?.durationMs
      }));
      setConsoleHistory(history);
    }
  }, [activeTab, tasks]);

  // Auto-scroll console
  useEffect(() => {
    if (consoleEndRef.current) {
      consoleEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [consoleHistory]);

  // Cleanup pollers on unmount
  useEffect(() => {
    return () => {
      Object.values(consolePollers.current).forEach(clearInterval);
      if (executePollerRef.current) clearInterval(executePollerRef.current);
    };
  }, []);

  // Poll for execute result
  const pollExecuteResult = useCallback((taskId) => {
    if (executePollerRef.current) clearInterval(executePollerRef.current);

    setExecuteResult({ taskId, status: 'sent', polling: true });

    const pollId = setInterval(async () => {
      try {
        const taskData = await agentService.getTask(agentId, taskId);
        if (['completed', 'failed', 'timeout', 'cancelled'].includes(taskData.status)) {
          clearInterval(pollId);
          executePollerRef.current = null;
          setExecuteResult({
            taskId,
            status: taskData.status,
            polling: false,
            command: taskData.command,
            executorType: taskData.executorType,
            exitCode: taskData.result?.exitCode,
            stdout: taskData.result?.stdout || '',
            stderr: taskData.result?.stderr || '',
            error: taskData.result?.error || '',
            durationMs: taskData.result?.durationMs,
            startTime: taskData.result?.startTime,
            endTime: taskData.result?.endTime
          });
        } else {
          setExecuteResult(prev => prev ? { ...prev, status: taskData.status } : prev);
        }
      } catch (err) {
        // ignore poll errors
      }
    }, 2000);
    executePollerRef.current = pollId;

    // Stop after 5 min
    setTimeout(() => {
      if (executePollerRef.current === pollId) {
        clearInterval(pollId);
        executePollerRef.current = null;
        setExecuteResult(prev => prev?.polling ? { ...prev, polling: false, status: 'timeout' } : prev);
      }
    }, 300000);
  }, [agentId]);

  const handleSendTask = async (e) => {
    e.preventDefault();
    setTaskSubmitting(true);
    setTaskResult(null);
    setExecuteResult(null);

    try {
      // Get selected project from localStorage (set by Layout component)
      const lastProjectId = localStorage.getItem('lastSelectedProject');
      const technique = techniques.find(t => t.technique_id === taskForm.techniqueId);
      const result = await agentService.sendTask(agentId, {
        ...taskForm,
        techniqueName: technique?.name,
        project: lastProjectId || undefined
      });

      // Start polling for the result
      if (result.task?.taskId) {
        pollExecuteResult(result.task.taskId);
      }

      fetchAgent();
      setTaskForm({
        name: '',
        executorType: agent?.capabilities?.defaultExecutor || 'shell',
        command: '',
        timeout: 300,
        cleanupCommand: '',
        techniqueId: ''
      });
    } catch (err) {
      setTaskResult({ success: false, message: err.response?.data?.message || err.message });
    } finally {
      setTaskSubmitting(false);
    }
  };

  const handleCancelTask = async (taskId) => {
    try {
      await agentService.cancelTask(agentId, taskId);
      fetchAgent();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRefresh = async () => {
    try {
      await agentService.refreshAgent(agentId);
      setTimeout(fetchAgent, 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  // Console: send command
  const handleConsoleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!consoleCommand.trim() || consoleSending) return;

    const cmd = consoleCommand.trim();
    setConsoleSending(true);
    setConsoleCommand('');

    const entry = {
      command: cmd,
      executor: consoleExecutor,
      status: 'running',
      taskId: null,
      timestamp: new Date().toISOString(),
      stdout: '',
      stderr: '',
      exitCode: null,
      durationMs: null
    };
    setConsoleHistory(prev => [...prev, entry]);
    const entryIdx = consoleHistory.length;

    try {
      const result = await agentService.sendConsoleCommand(agentId, cmd, consoleExecutor);

      setConsoleHistory(prev => {
        const updated = [...prev];
        if (updated[entryIdx]) {
          updated[entryIdx] = { ...updated[entryIdx], taskId: result.taskId, status: result.sent ? 'sent' : 'pending' };
        }
        return updated;
      });

      // Poll for result
      const pollId = setInterval(async () => {
        try {
          const taskData = await agentService.getTask(agentId, result.taskId);
          if (['completed', 'failed', 'timeout', 'cancelled'].includes(taskData.status)) {
            clearInterval(pollId);
            delete consolePollers.current[result.taskId];
            setConsoleHistory(prev => {
              const updated = [...prev];
              const idx = updated.findIndex(e => e.taskId === result.taskId);
              if (idx !== -1) {
                updated[idx] = {
                  ...updated[idx],
                  status: taskData.status,
                  stdout: taskData.result?.stdout || '',
                  stderr: taskData.result?.stderr || '',
                  exitCode: taskData.result?.exitCode,
                  durationMs: taskData.result?.durationMs
                };
              }
              return updated;
            });
          }
        } catch (pollErr) {
          // ignore
        }
      }, 2000);
      consolePollers.current[result.taskId] = pollId;

      setTimeout(() => {
        if (consolePollers.current[result.taskId]) {
          clearInterval(consolePollers.current[result.taskId]);
          delete consolePollers.current[result.taskId];
        }
      }, 120000);

    } catch (err) {
      setConsoleHistory(prev => {
        const updated = [...prev];
        if (updated[entryIdx]) {
          updated[entryIdx] = { ...updated[entryIdx], status: 'failed', stderr: err.response?.data?.message || err.message };
        }
        return updated;
      });
    } finally {
      setConsoleSending(false);
    }
  };

  const handleConsoleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleConsoleSubmit();
    }
  };

  // Activity helpers
  const getActivityStyle = (event) => {
    const styles = {
      connected: 'text-green-400',
      disconnected: 'text-red-400',
      registered: 'text-blue-400',
      approved: 'text-green-400',
      revoked: 'text-orange-400',
      task_sent: 'text-blue-400',
      task_completed: 'text-green-400',
      task_failed: 'text-red-400',
      heartbeat_lost: 'text-yellow-400',
      info_refreshed: 'text-cyan-400'
    };
    return styles[event] || 'text-gray-400';
  };

  const filteredActivity = (agent?.activityLog || [])
    .filter(a => activityFilter === 'all' || a.event === activityFilter)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded">
          {t('agents.notFound')}
        </div>
      </div>
    );
  }

  const statusBadge = (status) => {
    const colors = {
      completed: 'bg-green-900 text-green-300',
      running: 'bg-blue-900 text-blue-300',
      sent: 'bg-blue-900 text-blue-300',
      pending: 'bg-yellow-900 text-yellow-300',
      failed: 'bg-red-900 text-red-300',
      timeout: 'bg-orange-900 text-orange-300',
      cancelled: 'bg-gray-700 text-gray-300'
    };
    return colors[status] || 'bg-gray-700 text-gray-300';
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="flex items-center gap-3">
            <Link to="/agents" className="text-gray-400 hover:text-white">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <span className="text-3xl">{agentService.getPlatformIcon(agent.platform)}</span>
            <h1 className="text-3xl font-bold text-white">{agent.name}</h1>
            <span className={`px-2 py-1 rounded text-sm ${
              agent.status === 'online' ? 'bg-green-900 text-green-300' :
              agent.status === 'busy' ? 'bg-yellow-900 text-yellow-300' :
              'bg-gray-700 text-gray-300'
            }`}>
              {t(`agents.${agent.status}`) || agent.status}
            </span>
            {!agent.isApproved && (
              <span className="bg-yellow-900 text-yellow-300 px-2 py-1 rounded text-sm">
                {t('agents.pendingApproval')}
              </span>
            )}
          </div>
          <p className="text-gray-400 mt-1">{agent.systemInfo?.hostname} - {agent.platform}</p>
        </div>
        <button
          onClick={handleRefresh}
          className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
          </svg>
          {t('common.refresh')}
        </button>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-700">
        {['overview', 'tasks', 'console', 'execute', 'activity'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-2 px-1 ${activeTab === tab ? 'text-red-500 border-b-2 border-red-500' : 'text-gray-400 hover:text-white'}`}
          >
            {t(`agents.${tab}`)}
          </button>
        ))}
      </div>

      {/* ===== OVERVIEW TAB ===== */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* System Info */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">{t('agents.systemInfo')}</h2>
            <div className="space-y-3">
              {[
                ['hostname', agent.systemInfo?.hostname],
                ['os', agent.systemInfo?.os],
                ['version', agent.systemInfo?.osVersion],
                ['architecture', agent.systemInfo?.architecture],
              ].map(([key, val]) => (
                <div key={key} className="flex justify-between">
                  <span className="text-gray-400">{t(`agents.${key}`)}</span>
                  <span className="text-white">{val || 'N/A'}</span>
                </div>
              ))}
              <div className="flex justify-between">
                <span className="text-gray-400">{t('agents.username')}</span>
                <span className="text-white">
                  {agent.systemInfo?.username || 'N/A'}
                  {agent.systemInfo?.isAdmin && <span className="ml-1 text-red-400">({t('agents.admin')})</span>}
                </span>
              </div>
              {[
                ['domain', agent.systemInfo?.domain],
                ['cpuCores', agent.systemInfo?.cpuCores],
              ].map(([key, val]) => (
                <div key={key} className="flex justify-between">
                  <span className="text-gray-400">{t(`agents.${key}`)}</span>
                  <span className="text-white">{val || 'N/A'}</span>
                </div>
              ))}
              <div className="flex justify-between">
                <span className="text-gray-400">{t('agents.memory')}</span>
                <span className="text-white">{agentService.formatBytes(agent.systemInfo?.totalMemory)}</span>
              </div>
            </div>
          </div>

          {/* Network Info */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">{t('agents.networkInfo')}</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">{t('agents.publicIP')}</span>
                <span className="text-white font-mono">{agent.systemInfo?.publicIP || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-400">{t('agents.localIPs')}</span>
                <div className="mt-1 flex flex-wrap gap-2">
                  {agent.systemInfo?.localIPs?.length > 0 ? (
                    agent.systemInfo.localIPs.map((ip, idx) => (
                      <span key={idx} className="bg-gray-700 text-white px-2 py-1 rounded font-mono text-sm">
                        {ip}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500">N/A</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Agent Info */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">{t('agents.agentInfo')}</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">{t('agents.agentId')}</span>
                <span className="text-white font-mono text-sm">{agent.agentId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('agents.version')}</span>
                <span className="text-white">{agent.version || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('agents.firstSeen')}</span>
                <span className="text-white">{new Date(agent.firstSeen).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('agents.lastSeen')}</span>
                <span className="text-white">{new Date(agent.lastSeen).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('agents.connectionCount')}</span>
                <span className="text-white">{agent.connectionCount}</span>
              </div>
            </div>
          </div>

          {/* Capabilities */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">{t('agents.capabilities')}</h2>
            <div className="space-y-3">
              <div>
                <span className="text-gray-400">{t('agents.executors')}</span>
                <div className="mt-1 flex flex-wrap gap-2">
                  {agent.capabilities?.executors?.map((exec, idx) => (
                    <span key={idx} className="bg-blue-900 text-blue-300 px-2 py-1 rounded text-sm">
                      {exec}
                    </span>
                  )) || <span className="text-gray-500">N/A</span>}
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('agents.defaultExecutor')}</span>
                <span className="text-white">{agent.capabilities?.defaultExecutor || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('agents.maxConcurrent')}</span>
                <span className="text-white">{agent.capabilities?.maxConcurrent || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('agents.maxExecutionTime')}</span>
                <span className="text-white">{agent.capabilities?.maxExecutionTime ? `${agent.capabilities.maxExecutionTime}s` : 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="bg-gray-800 rounded-lg p-6 lg:col-span-2">
            <h2 className="text-xl font-bold text-white mb-4">{t('agents.statistics')}</h2>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gray-900 rounded p-4 text-center">
                <div className="text-2xl font-bold text-green-500">{agent.stats?.tasksCompleted || 0}</div>
                <div className="text-gray-400 text-sm">{t('agents.completed')}</div>
              </div>
              <div className="bg-gray-900 rounded p-4 text-center">
                <div className="text-2xl font-bold text-red-500">{agent.stats?.tasksFailed || 0}</div>
                <div className="text-gray-400 text-sm">{t('agents.failed')}</div>
              </div>
              <div className="bg-gray-900 rounded p-4 text-center">
                <div className="text-2xl font-bold text-yellow-500">{agent.stats?.tasksTimeout || 0}</div>
                <div className="text-gray-400 text-sm">{t('agents.timeout')}</div>
              </div>
              <div className="bg-gray-900 rounded p-4 text-center">
                <div className="text-2xl font-bold text-blue-500">{agent.runningTasks?.length || 0}</div>
                <div className="text-gray-400 text-sm">{t('agents.running')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== TASKS TAB ===== */}
      {activeTab === 'tasks' && (
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr>
                <th className="text-left text-gray-400 px-4 py-3">{t('common.status')}</th>
                <th className="text-left text-gray-400 px-4 py-3">{t('common.name')}</th>
                <th className="text-left text-gray-400 px-4 py-3">{t('agents.command')}</th>
                <th className="text-left text-gray-400 px-4 py-3">{t('agents.technique')}</th>
                <th className="text-left text-gray-400 px-4 py-3">{t('agents.created')}</th>
                <th className="text-left text-gray-400 px-4 py-3">{t('agents.duration')}</th>
                <th className="text-left text-gray-400 px-4 py-3">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-gray-500 py-8">
                    {t('agents.noTasksYet')}
                  </td>
                </tr>
              ) : (
                tasks.map((task) => (
                  <tr key={task.taskId} className="border-t border-gray-700 hover:bg-gray-700/30">
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-sm ${statusBadge(task.status)}`}>
                        {task.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white text-sm">{task.name || '-'}</td>
                    <td className="px-4 py-3">
                      <code className="text-green-400 text-sm bg-gray-900 px-2 py-1 rounded">
                        {task.command?.substring(0, 50)}{task.command?.length > 50 ? '...' : ''}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-gray-300 text-sm">{task.techniqueId || '-'}</td>
                    <td className="px-4 py-3 text-gray-400 text-sm">
                      {new Date(task.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-sm">
                      {task.result?.durationMs ? `${(task.result.durationMs / 1000).toFixed(2)}s` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setTaskResult({ task, showDetails: true })}
                          className="text-blue-400 hover:text-blue-300"
                          title={t('common.details')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        {(task.status === 'running' || task.status === 'sent' || task.status === 'pending') && (
                          <button
                            onClick={() => handleCancelTask(task.taskId)}
                            className="text-red-400 hover:text-red-300"
                            title={t('common.cancel')}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== CONSOLE TAB ===== */}
      {activeTab === 'console' && (
        <div className="bg-gray-800 rounded-lg overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 280px)' }}>
          {/* Header bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
            <span className="text-gray-400 text-sm font-medium">{t('agents.console')}</span>
            <button
              onClick={() => setConsoleHistory([])}
              className="text-gray-500 hover:text-gray-300 text-xs px-2 py-1 rounded hover:bg-gray-700"
            >
              {t('agents.clearConsole')}
            </button>
          </div>

          {/* Output area */}
          <div className="flex-1 overflow-y-auto p-4 bg-gray-900 font-mono text-sm space-y-3">
            {consoleHistory.length === 0 && (
              <div className="text-gray-500 text-center py-8">
                {t('agents.consoleEmpty')}
              </div>
            )}
            {consoleHistory.map((entry, idx) => (
              <div key={idx} className="border-b border-gray-800 pb-2">
                {/* Command line */}
                <div className="flex items-center gap-2">
                  <span className="text-purple-400 text-xs">{entry.executor}</span>
                  <span className="text-gray-600">$</span>
                  <span className="text-white">{entry.command}</span>
                  <span className="text-gray-700 text-xs ml-auto">{new Date(entry.timestamp).toLocaleTimeString()}</span>
                </div>
                {/* Status / output */}
                {entry.status === 'running' || entry.status === 'sent' || entry.status === 'pending' ? (
                  <div className="mt-1 flex items-center gap-2 text-blue-400">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-400"></div>
                    <span className="text-xs">{t('agents.waitingForResult')}</span>
                  </div>
                ) : (
                  <>
                    {entry.stdout && (
                      <pre className="mt-1 text-green-300 whitespace-pre-wrap break-all text-xs leading-relaxed">{entry.stdout}</pre>
                    )}
                    {entry.stderr && (
                      <pre className="mt-1 text-red-400 whitespace-pre-wrap break-all text-xs leading-relaxed">{entry.stderr}</pre>
                    )}
                    {!entry.stdout && !entry.stderr && entry.status === 'completed' && (
                      <div className="mt-1 text-gray-600 text-xs">{t('agents.noOutput')}</div>
                    )}
                    <div className="mt-1 text-gray-600 text-xs flex items-center gap-3">
                      <span className={entry.exitCode === 0 ? 'text-green-600' : entry.exitCode != null ? 'text-red-600' : 'text-gray-600'}>
                        exit: {entry.exitCode ?? '?'}
                      </span>
                      {entry.durationMs != null && <span>{entry.durationMs}ms</span>}
                      <span className={`${statusBadge(entry.status)} px-1 rounded text-xs`}>{entry.status}</span>
                    </div>
                  </>
                )}
              </div>
            ))}
            <div ref={consoleEndRef} />
          </div>

          {/* Input area */}
          <div className="p-3 bg-gray-800 border-t border-gray-700">
            {(!agent.isApproved || agent.status === 'offline') && (
              <div className="mb-2 text-yellow-500 text-xs">
                {!agent.isApproved ? t('agents.consoleAgentNotApproved') : t('agents.consoleAgentNotConnected')}
              </div>
            )}
            <div className="flex items-center gap-2">
              <select
                value={consoleExecutor}
                onChange={(e) => setConsoleExecutor(e.target.value)}
                className="bg-gray-700 text-white px-3 py-2 rounded text-sm w-32"
              >
                {(agent.capabilities?.executors || ['shell', 'powershell', 'cmd', 'bash', 'python']).map(exec => (
                  <option key={exec} value={exec}>{exec}</option>
                ))}
              </select>
              <textarea
                value={consoleCommand}
                onChange={(e) => setConsoleCommand(e.target.value)}
                onKeyDown={handleConsoleKeyDown}
                className="flex-1 bg-gray-900 text-white px-3 py-2 rounded font-mono text-sm resize-none"
                rows={1}
                placeholder={t('agents.consolePlaceholder')}
                disabled={!agent.isApproved || agent.status === 'offline'}
              />
              <button
                onClick={handleConsoleSubmit}
                disabled={!consoleCommand.trim() || consoleSending || !agent.isApproved || agent.status === 'offline'}
                className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white px-4 py-2 rounded text-sm font-medium"
              >
                {consoleSending ? '...' : 'Run'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== EXECUTE TAB ===== */}
      {activeTab === 'execute' && (
        <div className="space-y-6">
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">{t('agents.executeCommand')}</h2>

            {!agent.isApproved && (
              <div className="bg-yellow-900/50 border border-yellow-500 text-yellow-200 px-4 py-3 rounded mb-4">
                {t('agents.pendingApprovalMessage')}
              </div>
            )}

            <form onSubmit={handleSendTask} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">{t('agents.taskName')}</label>
                  <input
                    type="text"
                    value={taskForm.name}
                    onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                    className="w-full bg-gray-900 text-white px-3 py-2 rounded"
                    placeholder={t('agents.taskNamePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">{t('agents.executorType')}</label>
                  <select
                    value={taskForm.executorType}
                    onChange={(e) => setTaskForm({ ...taskForm, executorType: e.target.value })}
                    className="w-full bg-gray-900 text-white px-3 py-2 rounded"
                  >
                    {agent.capabilities?.executors?.map((exec) => (
                      <option key={exec} value={exec}>{exec}</option>
                    )) || (
                      <>
                        <option value="shell">Shell</option>
                        <option value="powershell">PowerShell</option>
                        <option value="cmd">CMD</option>
                        <option value="bash">Bash</option>
                        <option value="python">Python</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">{t('agents.command')} *</label>
                <textarea
                  value={taskForm.command}
                  onChange={(e) => setTaskForm({ ...taskForm, command: e.target.value })}
                  className="w-full bg-gray-900 text-white px-3 py-2 rounded font-mono h-32"
                  placeholder="whoami /all"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">{t('agents.mitreTechnique')}</label>
                  <input
                    type="text"
                    list="techniques-list"
                    value={taskForm.techniqueId}
                    onChange={(e) => setTaskForm({ ...taskForm, techniqueId: e.target.value })}
                    placeholder={t('agents.selectTechnique')}
                    className="w-full bg-gray-900 text-white px-3 py-2 rounded border border-gray-700 focus:border-blue-500 focus:outline-none"
                  />
                  <datalist id="techniques-list">
                    {techniques.map((tech) => (
                      <option key={tech.technique_id} value={tech.technique_id}>
                        {tech.technique_id} - {tech.name}
                      </option>
                    ))}
                  </datalist>
                  {taskForm.techniqueId && techniques.find(t => t.technique_id === taskForm.techniqueId) && (
                    <div className="text-xs text-gray-500 mt-1">
                      {techniques.find(t => t.technique_id === taskForm.techniqueId).name}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">{t('agents.timeoutSeconds')}</label>
                  <input
                    type="number"
                    value={taskForm.timeout}
                    onChange={(e) => setTaskForm({ ...taskForm, timeout: parseInt(e.target.value) })}
                    className="w-full bg-gray-900 text-white px-3 py-2 rounded"
                    min="1"
                    max="3600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">{t('agents.cleanupCommand')}</label>
                <input
                  type="text"
                  value={taskForm.cleanupCommand}
                  onChange={(e) => setTaskForm({ ...taskForm, cleanupCommand: e.target.value })}
                  className="w-full bg-gray-900 text-white px-3 py-2 rounded font-mono"
                  placeholder={t('agents.cleanupCommandPlaceholder')}
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={!agent.isApproved || taskSubmitting || agent.status === 'offline'}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-2 rounded flex items-center gap-2"
                >
                  {taskSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {t('agents.sending')}
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                      {t('agents.execute')}
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Error from sending */}
            {taskResult && !taskResult.success && !taskResult.showDetails && (
              <div className="mt-4 p-4 rounded bg-red-900/50 border border-red-500 text-red-200">
                {taskResult.message}
              </div>
            )}
          </div>

          {/* Live Execute Result */}
          {executeResult && (
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">{t('agents.taskResult')}</h3>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded text-sm ${statusBadge(executeResult.status)}`}>
                    {executeResult.status}
                  </span>
                  {executeResult.polling && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                  )}
                  <button
                    onClick={() => { setExecuteResult(null); if (executePollerRef.current) { clearInterval(executePollerRef.current); executePollerRef.current = null; } }}
                    className="text-gray-500 hover:text-gray-300"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>

              {executeResult.polling ? (
                <div className="flex items-center gap-3 text-blue-400 py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
                  <span>{t('agents.taskSentWatching')}</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Info grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-gray-900 rounded p-3">
                      <div className="text-gray-500 text-xs mb-1">{t('agents.exitCode')}</div>
                      <div className={`font-mono font-bold ${executeResult.exitCode === 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {executeResult.exitCode ?? 'N/A'}
                      </div>
                    </div>
                    <div className="bg-gray-900 rounded p-3">
                      <div className="text-gray-500 text-xs mb-1">{t('agents.duration')}</div>
                      <div className="text-white font-mono">
                        {executeResult.durationMs != null ? `${(executeResult.durationMs / 1000).toFixed(2)}s` : 'N/A'}
                      </div>
                    </div>
                    <div className="bg-gray-900 rounded p-3">
                      <div className="text-gray-500 text-xs mb-1">{t('agents.executorType')}</div>
                      <div className="text-white">{executeResult.executorType || 'N/A'}</div>
                    </div>
                    <div className="bg-gray-900 rounded p-3">
                      <div className="text-gray-500 text-xs mb-1">{t('common.status')}</div>
                      <div className={`font-bold ${executeResult.status === 'completed' ? 'text-green-400' : 'text-red-400'}`}>
                        {executeResult.status}
                      </div>
                    </div>
                  </div>

                  {/* Command */}
                  {executeResult.command && (
                    <div>
                      <div className="text-gray-500 text-xs mb-1">{t('agents.command')}</div>
                      <pre className="bg-gray-900 rounded p-3 text-green-400 font-mono text-sm overflow-x-auto whitespace-pre-wrap">{executeResult.command}</pre>
                    </div>
                  )}

                  {/* stdout */}
                  <div>
                    <div className="text-gray-500 text-xs mb-1">{t('agents.output')}</div>
                    {executeResult.stdout ? (
                      <pre className="bg-gray-900 rounded p-3 text-white font-mono text-sm overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap">{executeResult.stdout}</pre>
                    ) : (
                      <div className="bg-gray-900 rounded p-3 text-gray-600 text-sm italic">{t('agents.noOutput')}</div>
                    )}
                  </div>

                  {/* stderr */}
                  {executeResult.stderr && (
                    <div>
                      <div className="text-gray-500 text-xs mb-1">{t('agents.errorStderr')}</div>
                      <pre className="bg-gray-900 rounded p-3 text-red-400 font-mono text-sm overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">{executeResult.stderr}</pre>
                    </div>
                  )}

                  {/* error */}
                  {executeResult.error && (
                    <div>
                      <div className="text-gray-500 text-xs mb-1">{t('agents.errorMessage')}</div>
                      <div className="bg-red-900/30 border border-red-800 rounded p-3 text-red-300">{executeResult.error}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== ACTIVITY TAB ===== */}
      {activeTab === 'activity' && (
        <div>
          {/* Filter bar */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {['all', 'connected', 'disconnected', 'registered', 'approved', 'revoked', 'task_sent', 'task_completed', 'task_failed'].map(filter => (
              <button
                key={filter}
                onClick={() => setActivityFilter(filter)}
                className={`px-3 py-1 rounded text-sm capitalize ${
                  activityFilter === filter
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {filter === 'all' ? t('agents.activityAll') : filter.replace(/_/g, ' ')}
              </button>
            ))}
          </div>

          <div className="bg-gray-800 rounded-lg p-4">
            {filteredActivity.length === 0 ? (
              <div className="text-gray-500 text-center py-8">
                {t('agents.noActivityYet')}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredActivity.map((entry, idx) => {
                  const color = getActivityStyle(entry.event);
                  return (
                    <div key={idx} className="flex items-start gap-3 py-2 border-b border-gray-700 last:border-0">
                      <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${color.replace('text-', 'bg-')}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium capitalize ${color}`}>
                            {entry.event?.replace(/_/g, ' ')}
                          </span>
                          <span className="text-gray-500 text-xs">
                            {new Date(entry.timestamp).toLocaleString()}
                          </span>
                        </div>
                        {entry.details && (
                          <p className="text-gray-400 text-sm mt-0.5">{entry.details}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== TASK DETAILS MODAL ===== */}
      {taskResult?.showDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setTaskResult(null)}>
          <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">{t('agents.taskDetails')}</h2>
              <button
                onClick={() => setTaskResult(null)}
                className="text-gray-400 hover:text-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Info grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-gray-900 rounded p-3">
                  <div className="text-gray-500 text-xs mb-1">{t('common.status')}</div>
                  <span className={`px-2 py-1 rounded text-sm ${statusBadge(taskResult.task.status)}`}>
                    {taskResult.task.status}
                  </span>
                </div>
                <div className="bg-gray-900 rounded p-3">
                  <div className="text-gray-500 text-xs mb-1">{t('agents.exitCode')}</div>
                  <div className={`font-mono font-bold ${
                    taskResult.task.result?.exitCode === 0 ? 'text-green-400' :
                    taskResult.task.result?.exitCode != null ? 'text-red-400' : 'text-gray-400'
                  }`}>
                    {taskResult.task.result?.exitCode ?? 'N/A'}
                  </div>
                </div>
                <div className="bg-gray-900 rounded p-3">
                  <div className="text-gray-500 text-xs mb-1">{t('agents.executor')}</div>
                  <div className="text-white">{taskResult.task.executorType}</div>
                </div>
                <div className="bg-gray-900 rounded p-3">
                  <div className="text-gray-500 text-xs mb-1">{t('agents.duration')}</div>
                  <div className="text-white font-mono">
                    {taskResult.task.result?.durationMs != null ? `${(taskResult.task.result.durationMs / 1000).toFixed(2)}s` : 'N/A'}
                  </div>
                </div>
              </div>

              {/* Details row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-900 rounded p-3">
                  <div className="text-gray-500 text-xs mb-1">{t('common.name')}</div>
                  <div className="text-white">{taskResult.task.name || '-'}</div>
                </div>
                <div className="bg-gray-900 rounded p-3">
                  <div className="text-gray-500 text-xs mb-1">{t('agents.technique')}</div>
                  <div className="text-white">
                    {taskResult.task.techniqueId ? `${taskResult.task.techniqueId}${taskResult.task.techniqueName ? ` - ${taskResult.task.techniqueName}` : ''}` : '-'}
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-900 rounded p-3">
                  <div className="text-gray-500 text-xs mb-1">{t('agents.created')}</div>
                  <div className="text-white text-sm">{taskResult.task.createdAt ? new Date(taskResult.task.createdAt).toLocaleString() : 'N/A'}</div>
                </div>
                <div className="bg-gray-900 rounded p-3">
                  <div className="text-gray-500 text-xs mb-1">{t('agents.startedAt')}</div>
                  <div className="text-white text-sm">{taskResult.task.result?.startTime ? new Date(taskResult.task.result.startTime).toLocaleString() : 'N/A'}</div>
                </div>
                <div className="bg-gray-900 rounded p-3">
                  <div className="text-gray-500 text-xs mb-1">{t('agents.completedAt')}</div>
                  <div className="text-white text-sm">{taskResult.task.completedAt ? new Date(taskResult.task.completedAt).toLocaleString() : 'N/A'}</div>
                </div>
              </div>

              {/* Command */}
              <div>
                <div className="text-gray-500 text-xs mb-1">{t('agents.command')}</div>
                <pre className="bg-gray-900 rounded p-3 text-green-400 font-mono text-sm overflow-x-auto whitespace-pre-wrap">
                  {taskResult.task.command}
                </pre>
              </div>

              {/* stdout */}
              <div>
                <div className="text-gray-500 text-xs mb-1">{t('agents.output')}</div>
                {taskResult.task.result?.stdout ? (
                  <pre className="bg-gray-900 rounded p-3 text-white font-mono text-sm overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap">
                    {taskResult.task.result.stdout}
                  </pre>
                ) : (
                  <div className="bg-gray-900 rounded p-3 text-gray-600 text-sm italic">{t('agents.noOutput')}</div>
                )}
              </div>

              {/* stderr */}
              {taskResult.task.result?.stderr && (
                <div>
                  <div className="text-gray-500 text-xs mb-1">{t('agents.errorStderr')}</div>
                  <pre className="bg-gray-900 rounded p-3 text-red-400 font-mono text-sm overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">
                    {taskResult.task.result.stderr}
                  </pre>
                </div>
              )}

              {/* error */}
              {taskResult.task.result?.error && (
                <div>
                  <div className="text-gray-500 text-xs mb-1">{t('agents.errorMessage')}</div>
                  <div className="bg-red-900/30 border border-red-800 rounded p-3 text-red-300">
                    {taskResult.task.result.error}
                  </div>
                </div>
              )}

              {/* Cleanup info */}
              {taskResult.task.cleanupCommand && (
                <div>
                  <div className="text-gray-500 text-xs mb-1">{t('agents.cleanupCommand')}</div>
                  <pre className="bg-gray-900 rounded p-3 text-yellow-400 font-mono text-sm overflow-x-auto whitespace-pre-wrap">
                    {taskResult.task.cleanupCommand}
                  </pre>
                  {taskResult.task.result?.cleanupRan !== undefined && (
                    <div className="mt-1 text-xs text-gray-500">
                      Cleanup: {taskResult.task.result.cleanupRan ? 'executed' : 'not executed'}
                      {taskResult.task.result.cleanupError && <span className="text-red-400 ml-2">{taskResult.task.result.cleanupError}</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentDetail;
